import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { GOOGLE_SHEETS_CONFIG, getGoogleSheetsUrl } from '../config/googleSheets'
import CrimeStructureDiagram from './CrimeStructureDiagram'

interface GoogleSheetsTableProps {
  title?: string
  customRange?: string
  isAdditional?: boolean
}

interface TableData {
  headers: string[]
  rows: string[][]
}

interface AdditionalTableData {
  tables: Array<{
    title: string
    headers: string[]
    rows: string[][]
  }>
}

interface CacheEntry {
  data: TableData | AdditionalTableData
  timestamp: number
  isAdditional: boolean
  fetchTime: string
}

// Глобальный кэш для хранения данных
const dataCache = new Map<string, CacheEntry>()
const CACHE_DURATION = 15 * 60 * 1000 // 15 минут в миллисекундах

// Функция для получения данных из кэша
const getCachedData = (cacheKey: string): { data: TableData | AdditionalTableData, fetchTime: string } | null => {
  const cached = dataCache.get(cacheKey)
  if (!cached) return null
  
  const now = Date.now()
  if (now - cached.timestamp > CACHE_DURATION) {
    // Кэш устарел, удаляем его
    dataCache.delete(cacheKey)
    return null
  }
  
  return { data: cached.data, fetchTime: cached.fetchTime }
}

// Функция для сохранения данных в кэш
const setCachedData = (cacheKey: string, data: TableData | AdditionalTableData, isAdditional: boolean, fetchTime: string) => {
  dataCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    isAdditional,
    fetchTime
  })
}

const GoogleSheetsTable: React.FC<GoogleSheetsTableProps> = ({
  title = "Данные из Google Sheets",
  customRange,
  isAdditional = false
}) => {
  const [data, setData] = useState<TableData | null>(null)
  const [additionalData, setAdditionalData] = useState<AdditionalTableData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchTime, setFetchTime] = useState<string | null>(null)

    useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Создаем ключ кэша на основе диапазона и флага isAdditional
        const cacheKey = `${customRange}_${isAdditional}`
        
        // Проверяем кэш
        const cachedResult = getCachedData(cacheKey)
        if (cachedResult) {
          if (isAdditional) {
            setAdditionalData(cachedResult.data as AdditionalTableData)
          } else {
            setData(cachedResult.data as TableData)
          }
          setFetchTime(cachedResult.fetchTime)
          setLoading(false)
          return
        }
        
        if (customRange && customRange.includes(',')) {
           // Если есть множественные диапазоны, делаем отдельные запросы
           const ranges = customRange.split(',').map(range => range.trim())
           
           if (isAdditional) {
             // Специальная обработка для дополнительной информации
             const tables: Array<{title: string, headers: string[], rows: string[][]}> = []
             
             for (const range of ranges) {
               const url = `https://sheets.googleapis.com/v4/spreadsheets/${GOOGLE_SHEETS_CONFIG.SPREADSHEET_ID}/values/${range}?key=${GOOGLE_SHEETS_CONFIG.API_KEY}`
               const response = await fetch(url)
               
               if (response.ok) {
                 const result = await response.json()
                 
                 if (result.values && result.values.length > 0) {
                   const headers = result.values[0]
                   const rows = result.values.slice(1)
                   
                   // Создаем заголовок для таблицы на основе диапазона
                   const rangeTitle = `Таблица ${tables.length + 1}`
                   
                   tables.push({
                     title: rangeTitle,
                     headers,
                     rows
                   })
                 }
               }
             }
             
             if (tables.length > 0) {
               const additionalData = { tables }
               const currentTime = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
               setAdditionalData(additionalData)
               setFetchTime(currentTime)
               setCachedData(cacheKey, additionalData, true, currentTime)
             } else {
               setError('Данные не найдены')
             }
           } else {
             // Обычная обработка для других секций
             let allHeaders: string[] = []
             let allRows: string[][] = []
             
             for (const range of ranges) {
               const url = `https://sheets.googleapis.com/v4/spreadsheets/${GOOGLE_SHEETS_CONFIG.SPREADSHEET_ID}/values/${range}?key=${GOOGLE_SHEETS_CONFIG.API_KEY}`
               const response = await fetch(url)
               
               if (response.ok) {
                 const result = await response.json()
                 
                 if (result.values && result.values.length > 0) {
                   
                   // Если это первый диапазон, берем заголовки из первой строки
                   if (allHeaders.length === 0) {
                     allHeaders = result.values[0]
                     allRows.push(...result.values.slice(1))
                   } else {
                     // Если это не первый диапазон, проверяем, есть ли заголовки
                     const firstRow = result.values[0]
                     const hasHeaders = firstRow.some((cell: any) => 
                       typeof cell === 'string' && 
                       (cell.toLowerCase().includes('статья') || 
                        cell.toLowerCase().includes('преступление') || 
                        cell.toLowerCase().includes('наказание'))
                     )
                     
                     if (hasHeaders) {
                       // Если первая строка содержит заголовки, добавляем остальные строки
                       allRows.push(...result.values.slice(1))
                     } else {
                       // Если первая строка не содержит заголовки, добавляем все строки
                       allRows.push(...result.values)
                     }
                   }
                 } else {
                   // Диапазон пустой или не содержит данных
                 }
               } else {
                 // Ошибка запроса к диапазону
               }
             }
             
             if (allHeaders.length > 0 && allRows.length > 0) {
               const tableData = { headers: allHeaders, rows: allRows }
               const currentTime = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
               setData(tableData)
               setFetchTime(currentTime)
               setCachedData(cacheKey, tableData, false, currentTime)
             } else {
               setError('Данные не найдены')
             }
           }
                 } else {
           // Обычный запрос для одного диапазона
           const url = customRange 
             ? `https://sheets.googleapis.com/v4/spreadsheets/${GOOGLE_SHEETS_CONFIG.SPREADSHEET_ID}/values/${customRange}?key=${GOOGLE_SHEETS_CONFIG.API_KEY}`
             : getGoogleSheetsUrl()
           
           const response = await fetch(url)
           
           if (!response.ok) {
             throw new Error('Ошибка загрузки данных')
           }
           
           const result = await response.json()
           
           if (result.values && result.values.length > 0) {
             const headers = result.values[0]
             const rows = result.values.slice(1)
             
             const tableData = { headers, rows }
             const currentTime = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
             setData(tableData)
             setFetchTime(currentTime)
             setCachedData(cacheKey, tableData, false, currentTime)
           } else {
             setError('Данные не найдены')
           }
         }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [customRange])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-gray-400">Загрузка данных...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
        <div className="flex items-center">
          <span className="text-xl mr-2">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!data && !additionalData) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/20 p-6"
    >
      <h2 className="text-2xl font-bold text-white mb-6 text-center">
        {title}
      </h2>
      
      {isAdditional && additionalData ? (
        <div className="space-y-6">
          {/* Блок-схема "СОСТАВ ПРЕСТУПЛЕНИЯ" */}
          <CrimeStructureDiagram />
          
          {/* Компактный макет для дополнительной информации */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
           {additionalData.tables.map((table, tableIndex) => (
             <motion.div
               key={tableIndex}
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ delay: tableIndex * 0.1 }}
               className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-2"
             >
               
               <div className="overflow-x-auto">
                 <table className="w-full text-xs">
                   <thead>
                     <tr className="border-b border-white/20">
                       {table.headers.map((header, index) => (
                         <th
                           key={index}
                           className="px-1 py-1 text-left text-white/80 font-medium bg-white/5 text-xs"
                         >
                           {header}
                         </th>
                       ))}
                     </tr>
                   </thead>
                   <tbody>
                     {table.rows.map((row, rowIndex) => (
                       <motion.tr
                         key={rowIndex}
                         initial={{ opacity: 0 }}
                         animate={{ opacity: 1 }}
                         transition={{ delay: (tableIndex * 0.1) + (rowIndex * 0.05) }}
                         className="border-b border-white/5 hover:bg-white/5 transition-colors"
                       >
                         {row.map((cell, cellIndex) => (
                           <td
                             key={cellIndex}
                             className="px-1 py-1 text-white/70 text-xs"
                           >
                                                         {cell === 'TRUE' ? (
                               <div className="flex items-center justify-center">
                                 <div className="relative w-3 h-3 bg-gradient-to-br from-green-400 to-green-600 rounded shadow-lg flex items-center justify-center">
                                   <svg className="w-1.5 h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                   </svg>
                                 </div>
                               </div>
                             ) : cell === 'FALSE' ? (
                               <div className="flex items-center justify-center">
                                 <div className="relative w-3 h-3 bg-gradient-to-br from-red-400 to-red-600 rounded shadow-lg flex items-center justify-center">
                                   <svg className="w-1.5 h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                     <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                   </svg>
                                 </div>
                               </div>
                             ) : (
                               cell
                             )}
                          </td>
                        ))}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          ))}
          </div>
        </div>
      ) : data ? (
        // Обычная таблица для других секций
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                {data.headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-4 py-3 text-left text-white font-semibold bg-white/5"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, rowIndex) => (
                <motion.tr
                  key={rowIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: rowIndex * 0.1 }}
                  className="border-b border-white/10 hover:bg-white/5 transition-colors"
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-4 py-3 text-white/90"
                    >
                      {cell === 'TRUE' ? (
                        <div className="flex items-center justify-center">
                          <div className="relative w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-lg shadow-lg flex items-center justify-center transform transition-all duration-200 hover:scale-110">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-lg opacity-80"></div>
                            <svg className="w-4 h-4 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 to-transparent"></div>
                          </div>
                        </div>
                      ) : cell === 'FALSE' ? (
                        <div className="flex items-center justify-center">
                          <div className="relative w-6 h-6 bg-gradient-to-br from-red-400 to-red-600 rounded-lg shadow-lg flex items-center justify-center transform transition-all duration-200 hover:scale-110">
                            <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-600 rounded-lg opacity-80"></div>
                            <svg className="w-4 h-4 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 to-transparent"></div>
                          </div>
                        </div>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      
      <div className="mt-4 text-center text-sm text-gray-400">
        {fetchTime && `Обновлено: ${fetchTime}`}
      </div>
    </motion.div>
  )
}

export default GoogleSheetsTable
